import React, { useState, useContext } from 'react';
import { connect, useSelector } from 'react-redux';
import { ReduxState } from 'services/types';
import {
	ActiveState,
	RentalState,
	OrderProduct,
	SetProduct,
	Shop,
	Category,
	OpeningHoursWithExceptions,
	Duration,
	OrderObject,
	SetProductWithParentId,
	PartialOrder,
	OrderInfo,
	Shopper,
	PartialWithId,
	PurchaseTypes,
} from 'common/types';
import { getProductCodes } from 'services/utils';
import { sendContract } from 'services/utils/sendContractEmail';
import {
	setShowRentalViewFormErrors,
	setStartPrint,
	updateSelectedSetProducts,
	updateSelectedNonSetProductIds,
	setShowEarlyOrLatePickupDialog,
	updateActivePartialOrder,
	setHideProductCodeErrors,
} from 'actions';
import ChangeRentalState, { RentalStateCta } from './ChangeRentalState';
import {
	getNextProductReturnTime,
	getNewDateProperties,
	getDifferenceBetweenMomentsMinutes,
	getTimeNearestToNextQuarterHour,
} from 'common/utils/dateCalculations';
import {
	getNewActiveAndRentalState,
	getProductWithUpdatedReturnTimes,
	getStatusChangesForSalesProducts,
	removeEndTimesFromProduct,
	updateProductTimeOnBookingStart,
} from 'common/utils/rentalUtils';
import RentAgainDialog from './RentAgainDialog';
import EarlyOrLateDialog from './EarlyOrLateDialog';
import { ActionFeedbackContext } from '..';
import moment from 'moment-timezone';
import {
	updateOrderStateHistory,
	returnOtherActiveProductsWithSameProductCodes,
} from 'common/api/frontend';
import { hasUnreleasedDeposit } from 'common/modules/atoms/deposit';
import DepositReleaseDialog from '../components/DepositReleaseDialog';
import { cancelPayment } from '../PaymentOverview/PaymentsList/utils';
import errorHandler from 'common/services/errorHandler';
import { useTranslation } from 'services/localization';
import { orderHasDeliveryOrPickup } from 'common/modules/delivery/utils';
import * as RentalSelectors from 'selectors/RentalViewSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { api } from 'common/frontend/api';
import {
	getOrderProductWithSetProducts,
	isRequiredProductCodeFilled,
	isOrderProduct,
	MappedDataWithChanges,
	isSalesProduct,
} from 'common/modules/orders';
import { assignPartialData } from 'common/utils/partialData';
import { InventoryItemController } from 'common/modules/inventory';
import { notUndefined, switchUnreachable } from 'common/utils/common';
import { useFeatures } from 'hooks/useFeatures';
import { usePermissions } from 'hooks/usePermissions';
import { setOnboardingActionDone } from 'services/onboarding/utils';

interface StateToProps {
	categories: Category[];
	showRentalViewFormErrors: boolean;
	shop: Shop;
	showEarlyOrLatePickupDialog: boolean;
}

interface OwnProps {
	rental: OrderObject;
}

interface StartAndEndTimeChanges {
	keepOldStartTime?: boolean;
	keepOldEndTime?: boolean;
	success?: boolean;
}

interface DispatchProps {
	setShowRentalViewFormErrors: (show: boolean) => void;
	setStartPrint: (startPrint: boolean) => void;
	updateSelectedSetProducts: (selectedSetProducts: SetProductWithParentId[]) => void;
	updateSelectedNonSetProductIds: (selectedNonSetProductIds: string[]) => void;
	setShowEarlyOrLatePickupDialog: (show: boolean) => void;
	updateActivePartialOrder: (partialOrder: PartialOrder) => void;
	setHideProductCodeErrors: (hide: boolean) => void;
}

type Props = StateToProps & DispatchProps & OwnProps;

export const ChangeRentalStateContainer = ({ ...props }: Props) => {
	const { t } = useTranslation();
	const { userHasFeatures } = useFeatures();
	const { hasPermission } = usePermissions();
	const { rental, categories, shop } = props;
	const depositPayment = rental.rentalInfo.deposit;
	const depositPricing = rental.rentalInfo.pricing.deposit;
	const [openRentAgainDialog, setOpenRentAgainDialog] = useState(false);
	const activeState = rental.rentalInfo.activeState;
	const rentalState = rental.rentalInfo.rentalState;
	const channel = rental.rentalInfo.channel;
	const hasDelivery = orderHasDeliveryOrPickup(rental.orderDelivery);

	const orderHasRentals = useSelector(RentalSelectors.orderHasRentals);
	const { rentAgainCreated } = useContext(ActionFeedbackContext);
	const [depositWarningAction, setDepositWarningAction] = useState<
		null | 'CANCEL_ORDER' | 'END_ORDER'
	>(null);
	const openingHoursWithExceptions = useSelector(RentalSelectors.openingHoursWithExceptions);
	const shopAnalytics = useSelector(ShopSelectors.activeShopAnalytics);

	const handleUpdateOrder = async (args: {
		newRentalState: RentalState;
		newActiveState: ActiveState;
		startAndEndTimeChanges?: StartAndEndTimeChanges;
	}) => {
		const { newRentalState, newActiveState, startAndEndTimeChanges } = args;
		const updatedActiveRental = updateActiveOrder({
			order: rental,
			prevRentalState: rentalState,
			newActiveState,
			newRentalState,
			openingHoursWithExceptions,
			...startAndEndTimeChanges,
		});
		const {
			rentalInfo: newRentalInfo,
			products: newProducts,
			shoppers: newShoppers,
		} = updatedActiveRental;

		const newOrderObject: OrderObject = {
			rentalInfo: newRentalInfo.full,
			products: newProducts.full,
			shoppers: newShoppers.full,
		};
		props.updateActivePartialOrder({
			rentalInfo: newRentalInfo.changes,
			products: newProducts.changes,
			shoppers: newShoppers.changes,
		});
		updateOrderStateHistory({
			orderId: newRentalInfo.full.id,
			oldStates: {
				activeState,
				rentalState,
			},
			newStates: {
				activeState: newActiveState,
				rentalState: newRentalState,
			},
		});

		if (newRentalState === 'ACTIVE') {
			if (props.shop.shopInfo.printReceipt) {
				props.setStartPrint(true);
			}
			if (rental.rentalInfo.channel === 'STORE') {
				sendContract(newOrderObject, shop, categories);
			}
			returnOtherActiveProductsWithSameProductCodes({
				orderId: newRentalInfo.full.id,
				shopId: newRentalInfo.full.shopId,
				productCodes: [...new Set(getProductCodes(newOrderObject.products))],
			});
		}
		if (newRentalState === 'COMPLETED') {
			setOnboardingActionDone('BOOKING_COMPLETION', shopAnalytics);
		}
		props.updateSelectedNonSetProductIds([]);
		props.updateSelectedSetProducts([]);
		if (activeState === 'RENT_ENDED') {
			setOpenRentAgainDialog(true);
		}
	};

	const handleStartAction = async (
		action: 'START_ORDER' | 'FULFILL_ORDER',
		startAndEndTimeChanges?: StartAndEndTimeChanges,
	) => {
		if (!areAllProductInputsFilled()) {
			props.setShowRentalViewFormErrors(true);
			return;
		}
		if (!props.showEarlyOrLatePickupDialog) {
			const startDayMoment = moment(rental.rentalInfo.startDate);
			const differenceInStartTime = getDifferenceBetweenMomentsMinutes(moment(), startDayMoment);
			if ((differenceInStartTime > 15 || differenceInStartTime < -15) && orderHasRentals) {
				props.setShowEarlyOrLatePickupDialog(true);
				return;
			}
		}
		props.setShowRentalViewFormErrors(false);

		const products = rental.products;
		const salesProducts = products.filter(isSalesProduct);
		const statusChanges = getStatusChangesForSalesProducts(action);
		if (!!statusChanges && salesProducts.length > 0) {
			const itemCodes = salesProducts.flatMap((p) => p.summary.itemCodes).filter(notUndefined);
			const { currentStatus, targetStatus } = statusChanges;

			const updateInventoryItems = async () => {
				try {
					props.setHideProductCodeErrors(true);
					await InventoryItemController.handleInventoryItemStatusChanges(api(), {
						shopId: shop.readOnly?.shopId!,
						products: salesProducts,
						itemCodes,
						currentStatus,
						targetStatus,
					});
					props.setHideProductCodeErrors(false);
				} catch (err) {
					errorHandler.report(err);
				}
			};
			updateInventoryItems();
		}

		const { newRentalState, newActiveState } = getNewActiveAndRentalState({
			action,
			channel,
			hasDelivery,
		});

		handleUpdateOrder({
			newRentalState,
			newActiveState,
			startAndEndTimeChanges,
		});
	};

	const handleEndAction = async (startAndEndTimeChanges?: StartAndEndTimeChanges) => {
		if (hasUnreleasedDeposit(depositPricing, depositPayment) && !depositWarningAction) {
			setDepositWarningAction('END_ORDER');
			return;
		}

		if (!orderHasRentals) {
			const salesProducts = rental.products.filter((p) => p.purchaseType === PurchaseTypes.sales);
			updateInventoryStatusSold(salesProducts);
		}

		const { newRentalState, newActiveState } = getNewActiveAndRentalState({
			action: 'END_ORDER',
			channel,
			hasDelivery,
		});

		handleUpdateOrder({
			newRentalState,
			newActiveState,
			startAndEndTimeChanges,
		});
	};

	const handleCancelAction = async (
		action: 'CANCEL_ORDER_START' | 'REOPEN_ORDER' | 'CANCEL_ORDER_FULFILLMENT' | 'CANCEL_ORDER',
	) => {
		if (action === 'CANCEL_ORDER') {
			if (!window.confirm(t('cancelRentalConfirm', 'Cancel the order?'))) return;
			if (hasUnreleasedDeposit(depositPricing, depositPayment) && !depositWarningAction) {
				setDepositWarningAction('CANCEL_ORDER');
				return;
			}
		}

		if (
			action === 'CANCEL_ORDER_START' &&
			!window.confirm(
				t(
					'cancelStartingRental',
					'Cancel start of the order? The order start time will be removed.',
				),
			)
		) {
			return;
		}

		const salesProducts = rental.products.filter(isSalesProduct);
		const statusChanges = getStatusChangesForSalesProducts(action);
		if (!!statusChanges && salesProducts.length > 0) {
			const { currentStatus, targetStatus } = statusChanges;
			const itemCodes = salesProducts.flatMap((p) => p.summary.itemCodes).filter(notUndefined);
			const updateInventoryItems = async () => {
				try {
					props.setHideProductCodeErrors(true);
					await InventoryItemController.handleInventoryItemStatusChanges(api(), {
						shopId: shop.readOnly?.shopId!,
						products: salesProducts,
						itemCodes,
						currentStatus,
						targetStatus,
					});
					props.setHideProductCodeErrors(false);
				} catch (err) {
					errorHandler.report(err);
				}
			};
			updateInventoryItems();
		}

		const { newRentalState, newActiveState } = getNewActiveAndRentalState({
			action,
			channel,
			hasDelivery,
		});

		const updatedActiveRental = updateActiveOrder({
			order: rental,
			prevRentalState: rentalState,
			newRentalState,
			newActiveState,
			openingHoursWithExceptions,
		});

		const {
			rentalInfo: newRentalInfo,
			products: newProducts,
			shoppers: newShoppers,
		} = updatedActiveRental;
		props.updateActivePartialOrder({
			rentalInfo: newRentalInfo.changes,
			products: newProducts.changes,
			shoppers: newShoppers.changes,
		});
		updateOrderStateHistory({
			orderId: newRentalInfo.full.id,
			oldStates: {
				activeState,
				rentalState,
			},
			newStates: {
				activeState: newActiveState,
				rentalState: newRentalState,
			},
		});
		props.updateSelectedNonSetProductIds([]);
		props.updateSelectedSetProducts([]);
		setOnboardingActionDone('BOOKING_COMPLETION', shopAnalytics);
	};

	const updateInventoryStatusSold = (products: OrderProduct[]) => {
		const itemCodes = products.flatMap((p) => p.summary.itemCodes).filter(notUndefined);
		const updateInventoryItems = async () => {
			try {
				props.setHideProductCodeErrors(true);
				await InventoryItemController.handleInventoryItemStatusChanges(api(), {
					shopId: shop.readOnly?.shopId!,
					products,
					itemCodes,
					currentStatus: 'IN_USE',
					targetStatus: 'SOLD',
				});
				props.setHideProductCodeErrors(false);
			} catch (err) {
				errorHandler.report(err);
			}
		};
		updateInventoryItems();
	};

	const handleRentAgainDialogClose = (success: boolean = false) => {
		if (success) {
			rentAgainCreated();
		}
		setOpenRentAgainDialog(false);
	};

	const handleEarlyOrLateDialogClose = (startAndEndTimeChanges?: StartAndEndTimeChanges) => {
		const startAndEndTimeChange = {
			keepOldStartTime: startAndEndTimeChanges?.keepOldStartTime ?? false,
			keepOldEndTime: startAndEndTimeChanges?.keepOldEndTime ?? false,
		};
		if (startAndEndTimeChanges?.success) {
			if (rentalState === 'BOOKED') {
				handleStartAction('START_ORDER', startAndEndTimeChange);
			}
			if (rentalState === 'COMPLETED') {
				handleEndAction(startAndEndTimeChange);
			}
		}
		props.setShowEarlyOrLatePickupDialog(false);
	};

	const areAllProductInputsFilled = () => {
		const allProductsWithoutSets = rental.products
			.flatMap((p) => getOrderProductWithSetProducts(p))
			.filter((p) => !p.set);
		return allProductsWithoutSets.every((product) => {
			if (!isOrderProduct(product) && product.removedFromParent) return true;
			if (
				!isDinCollected(product) ||
				!isProductCustomValuesCollected(product) ||
				!isRequiredProductCodeFilled(product)
			) {
				return false;
			}
			return true;
		});
	};

	const isDinCollected = (product: OrderProduct | SetProduct) => {
		if (!product.collectDin || (product as SetProduct).removedFromParent) {
			return true;
		}
		if (
			!product.dinValues ||
			(!product.dinValues.customDin &&
				product.dinValues.customDin !== 0 &&
				!product.dinValues.calculatedDin &&
				product.dinValues.calculatedDin !== 0)
		) {
			return false;
		}
		return true;
	};

	const isProductCustomValuesCollected = (product: OrderProduct | SetProduct) => {
		const { customValuesToCollect, customValues } = product;
		if (
			!customValuesToCollect ||
			!customValuesToCollect.length ||
			customValuesToCollect.every((v) => v.name == null || v.name === '') ||
			(product as SetProduct).removedFromParent
		) {
			return true;
		}
		if (!customValues) {
			return false;
		}
		for (const customValueToCollect of customValuesToCollect) {
			const name = customValueToCollect.name;
			const customValue = customValues.filter((cv) => cv.name === name)[0];
			if (!customValue || (!customValue.value && customValue.value !== 0)) {
				return false;
			}
		}
		return true;
	};

	const handleDepositRelease = async () => {
		if (hasUnreleasedDeposit(depositPricing, depositPayment)) {
			const orderId = rental.rentalInfo.id;
			const shopId = rental.rentalInfo.shopId;
			const depositTransactions = await api()
				.transactions.get.where('orderId', '==', orderId)
				.where('shopId', '==', shopId)
				.where('paymentType', '==', 'DEPOSIT')
				.get();
			for (const transaction of depositTransactions) {
				cancelPayment(transaction).catch((e) => errorHandler.report(e));
			}
		}

		switch (depositWarningAction) {
			case 'END_ORDER': {
				handleEndAction();
				break;
			}
			case 'CANCEL_ORDER': {
				handleCancelAction('CANCEL_ORDER');
				break;
			}
			default: {
				break;
			}
		}
		setDepositWarningAction(null);
	};

	const ctas = ((): (RentalStateCta | null)[] => {
		switch (rentalState) {
			case 'BOOKED': {
				return [
					orderHasRentals
						? {
								label: t('orderActions.start', 'Start'),
								onClick: () => handleStartAction('START_ORDER'),
								dataQa: 'cancelButton',
								style: 'success',
						  }
						: {
								label: t('orderActions.fulfill', 'Fulfill'),
								style: 'success',
								dataQa: 'cancelButton',
								onClick: () => handleStartAction('FULFILL_ORDER'),
						  },
					{
						label: t('orderActions.cancelOrder', 'Cancel order'),
						style: 'danger',
						dataQa: 'cancelButton',
						onClick: () => handleCancelAction('CANCEL_ORDER'),
						disabled: !hasPermission('cancel_booking'),
					},
				];
			}
			case 'ACTIVE': {
				return [
					{
						label: t('orderActions.endOrder', 'End order'),
						onClick: handleEndAction,
						style: 'primary',
						dataQa: 'startButton',
					},
					{
						label: t('orderActions.cancelStart', 'Cancel start'),
						onClick: () => handleCancelAction('CANCEL_ORDER_START'),
						style: 'danger',
						dataQa: 'cancelButton',
					},
				];
			}
			case 'COMPLETED': {
				return [
					orderHasRentals && userHasFeatures('NEW_RENTAL')
						? {
								label: t('orderActions.duplicateOrder', 'Duplicate order'),
								onClick: () => {
									setOpenRentAgainDialog(true);
								},
								style: 'primary_outlined',
						  }
						: null,
					orderHasRentals
						? {
								label: t('orderActions.reOpenOrder', 'Re-open order'),
								onClick: () => handleCancelAction('REOPEN_ORDER'),
								dataQa: 'cancelButton',
								style: 'danger',
						  }
						: {
								label: t('orderActions.cancelFulfillment', 'Cancel fulfillment'),
								onClick: () => handleCancelAction('CANCEL_ORDER_FULFILLMENT'),
								dataQa: 'cancelButton',
								style: 'danger',
						  },
				];
			}
			default:
				return [];
		}
	})();

	return (
		<>
			<ChangeRentalState
				ctas={ctas}
				errorMessage={
					props.showRentalViewFormErrors
						? t('fillRedFieldsFirst2', `Please fill fields marked in red`)
						: null
				}
			/>
			{openRentAgainDialog && (
				<RentAgainDialog
					open={openRentAgainDialog}
					rental={rental}
					onClose={handleRentAgainDialogClose}
				/>
			)}
			{props.showEarlyOrLatePickupDialog && (
				<EarlyOrLateDialog
					open={true}
					rental={rental}
					onClose={(startAndEndTimeChanges) => handleEarlyOrLateDialogClose(startAndEndTimeChanges)}
				/>
			)}
			{!!depositWarningAction && !!depositPricing && (
				<DepositReleaseDialog
					deposit={depositPricing}
					payment={depositPayment}
					onCancel={() => setDepositWarningAction(null)}
					onConfirm={handleDepositRelease}
				/>
			)}
		</>
	);
};

const mapStateToProps = ({ stock, view, shop }: ReduxState): StateToProps => {
	const categories = stock.categories.data!;
	return {
		categories,
		showRentalViewFormErrors: view.showRentalViewFormErrors,
		shop: shop.shop.data!,
		showEarlyOrLatePickupDialog: view.showEarlyOrLatePickupDialog,
	};
};

const dispatchProps: DispatchProps = {
	setShowRentalViewFormErrors,
	setStartPrint,
	updateSelectedSetProducts,
	updateSelectedNonSetProductIds,
	setShowEarlyOrLatePickupDialog,
	updateActivePartialOrder,
	setHideProductCodeErrors,
};

export default connect(mapStateToProps, dispatchProps)(ChangeRentalStateContainer);

export const updateActiveOrder = (args: {
	order: OrderObject;
	prevRentalState: RentalState;
	newRentalState: RentalState;
	newActiveState: ActiveState;
	openingHoursWithExceptions: OpeningHoursWithExceptions;
	keepOldStartTime?: boolean;
	keepOldEndTime?: boolean;
}): {
	rentalInfo: MappedDataWithChanges<OrderInfo>;
	products: MappedDataWithChanges<OrderProduct[]>;
	shoppers: MappedDataWithChanges<Shopper[]>;
} => {
	const {
		order,
		prevRentalState,
		newRentalState,
		newActiveState,
		openingHoursWithExceptions,
	} = args;
	let keepOldEndTime = args.keepOldEndTime;
	let keepOldStartTime = args.keepOldStartTime;
	const { shoppers, products } = order;
	const rentalInfo = {
		...order.rentalInfo,
		activeState: newActiveState,
		rentalState: newRentalState,
	};
	let partialRentalInfo: Partial<OrderInfo> = {
		activeState: newActiveState,
		rentalState: newRentalState,
	};
	let partialProducts: {
		[productId: string]: Partial<OrderProduct>;
	} = {};
	let partialShoppers: {
		[shopperId: string]: Partial<Shopper>;
	} = {};
	// add start and end time for rental only if we are starting rental not cancelling return.

	const orderHasRentals = rentalInfo.purchaseTypes.includes(PurchaseTypes.rental);
	switch (newRentalState) {
		case 'BOOKED':
			shoppers.forEach((shopper) => {
				partialShoppers[shopper.id] = { ...partialShoppers[shopper.id], productsReturned: false };
			});
			const differenceInStartTimeAndInitTime =
				moment(rentalInfo.initialStartDate).diff(moment(rentalInfo.startDate)) !== 0;
			products.forEach((product) => {
				const partialChanges = {
					...updateProductTimeOnBookingStart(product, {
						newStartTime: differenceInStartTimeAndInitTime
							? rentalInfo.initialStartDate
							: undefined,
						openingHoursWithExceptions,
					}),
					fulfillmentDate: null,
				};

				partialProducts[product.id] = { ...partialProducts[product.id], ...partialChanges };
			});
			partialRentalInfo = {
				...partialRentalInfo,
				returnTimeNext: null,
				prepared: false,
				startDateException: false,
				...(differenceInStartTimeAndInitTime && { startDate: rentalInfo.initialStartDate }),
			};
			break;
		case 'ACTIVE':
			if (prevRentalState === 'BOOKED') {
				let newStartDate = rentalInfo.startDate;
				const now = moment.utc().toISOString();
				const startDateDiffMinutesToCurrent = moment(newStartDate).diff(moment(), 'minutes');
				const startingLate = startDateDiffMinutesToCurrent < 0;
				if (Math.abs(startDateDiffMinutesToCurrent) > 15 && !keepOldStartTime) {
					newStartDate =
						startingLate && keepOldEndTime
							? newStartDate
							: getTimeNearestToNextQuarterHour().toISOString();
				} else {
					keepOldEndTime = true;
				}
				products.forEach((product) => {
					const { rentalDurationInSeconds, durationType, durationName } = product;
					const productDuration: Duration = {
						durationInSeconds: rentalDurationInSeconds || 0,
						durationType: durationType || '24h',
						durationName: durationName || null,
					};
					const productEndDate = product.endDate;
					const newDateProperties = getNewDateProperties(
						{
							new: {
								startDate: newStartDate,
								endDate: (keepOldEndTime && productEndDate) || undefined,
								duration: (keepOldEndTime && productDuration) || undefined,
							},
							old: { duration: productDuration, endDate: productEndDate || undefined },
						},
						openingHoursWithExceptions,
					);
					const newProductDuration = newDateProperties.duration || productDuration;
					const newProductEndDate = newDateProperties.endDate || productEndDate;
					const partialChanges: Partial<OrderProduct> = {
						endDate: newProductEndDate,
						durationType: newProductDuration.durationType,
						rentalDurationInSeconds: newProductDuration.durationInSeconds,
						startDate: newStartDate,
						fulfillmentDate: isSalesProduct(product) ? now : null,
					};
					partialProducts[product.id] = { ...partialProducts[product.id], ...partialChanges };
				});

				partialRentalInfo = {
					...partialRentalInfo,
					startDate: newStartDate,
					...(keepOldEndTime !== undefined && { startDateException: keepOldEndTime }),
				};
			} else if (prevRentalState === 'COMPLETED') {
				shoppers.forEach((shopper) => {
					partialShoppers[shopper.id] = { ...partialShoppers[shopper.id], productsReturned: false };
				});
				products.forEach((product) => {
					const partialChanges = removeEndTimesFromProduct(product);
					partialProducts[product.id] = { ...partialProducts[product.id], ...partialChanges };
				});
				partialRentalInfo = {
					...partialRentalInfo,
					endDateReturned: null,
					...(!orderHasRentals && { rentalState: 'BOOKED', activeState: 'ONLINE_BOOKED' }),
				};
			}
			const partialProductsArray: PartialWithId<OrderProduct>[] = Object.entries(
				partialProducts,
			).map(([productId, productChanges]) => ({ id: productId, data: productChanges }));
			const fullProducts = assignPartialData(products, partialProductsArray);
			const returnTimeNext = getNextProductReturnTime(fullProducts);
			partialRentalInfo = {
				...partialRentalInfo,
				returnTimeNext,
			};
			break;
		case 'COMPLETED':
			const now = moment.utc().toISOString();
			partialRentalInfo = {
				...partialRentalInfo,
				returnTimeNext: null,
				endDateReturned: rentalInfo.endDateReturned || now,
			};
			const isCancelled = newActiveState === 'RENT_CANCEL';
			products.forEach((product) => {
				switch (product.purchaseType) {
					case PurchaseTypes.rental: {
						partialProducts[product.id] = {
							...partialProducts[product.id],
							...getProductWithUpdatedReturnTimes(product, now),
							...(isCancelled && { cancelled: true }),
						};
						break;
					}
					case PurchaseTypes.sales: {
						partialProducts[product.id] = {
							...partialProducts[product.id],
							...(!product.fulfillmentDate && !isCancelled && { fulfillmentDate: now }),
							...(isCancelled && { cancelled: true }),
						};
						break;
					}
					default:
						switchUnreachable(product.purchaseType);
						break;
				}
			});
			shoppers.forEach((shopper) => {
				partialShoppers[shopper.id] = { ...partialShoppers[shopper.id], productsReturned: true };
			});
			break;
		default:
			break;
	}
	const partialProductsArray: PartialWithId<OrderProduct>[] = Object.entries(
		partialProducts,
	).map(([productId, productChanges]) => ({ id: productId, data: productChanges }));
	const partialShoppersArray: PartialWithId<Shopper>[] = Object.entries(
		partialShoppers,
	).map(([shopperId, shopperChanges]) => ({ id: shopperId, data: shopperChanges }));
	const fullProducts = assignPartialData(products, partialProductsArray);
	const fullShoppers = assignPartialData(shoppers, partialShoppersArray);
	const fullRentalInfo = {
		...rentalInfo,
		...partialRentalInfo,
	};
	return {
		rentalInfo: {
			changes: {
				id: rentalInfo.id,
				data: partialRentalInfo,
			},
			full: fullRentalInfo,
		},
		products: {
			changes: partialProductsArray,
			full: fullProducts,
		},
		shoppers: {
			changes: partialShoppersArray,
			full: fullShoppers,
		},
	};
};
